// apps/client/src/pages/summary/Summary.tsx
import { ResearchActivity, ResearchActivityType, UserRole } from '@avanzu/types';
import { calcProjectStartAndEndDates } from '@avanzu/utils';
import {
  AssessmentOutlined,
  EditOutlined,
  ScienceOutlined,
  TrendingUpOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services';
import { fetchProjectActivities } from '../../services/projects';
import formatCurrency from '../../utils/formatCurrency';
import { LoadingComponent } from '../../components/LoadingComponent';
import HelpComponent from '../../components/HelpComponent';
import WorkbookDownloadButton from './components/WorkbookDownloadButton';
import PlanDownloadButton from './components/PlanDownloadButton';

export const SummaryPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

interface ActivitySummaryData {
  activityId: string;
  expenses: number;
  wages: number;
  super: number;
  assets: number;
  equipment: number;
  total: number;
}

interface ProjectSummaryData {
  totalExpenses: number;
  totalWages: number;
  totalSuper: number;
  totalAssets: number;
  totalEquipment: number;
  grandTotal: number;
  activities: ActivitySummaryData[];
  totalTaxOffset: number;
}

interface ProjectedSummaryData extends ProjectSummaryData {
  projectedTotalExpenses: number;
  projectedTotalWages: number;
  projectedTotalSuper: number;
  projectedTotalAssets: number;
  projectedTotalEquipment: number;
  projectedGrandTotal: number;
  projectedTotalTaxOffset: number;
  projectedActivities: ActivitySummaryData[];
  daysElapsed: number;
  totalDays: number;
  isCurrentYear: boolean;
}

export const Summary: React.FC = () => {
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [summaryData, setSummaryData] = useState<ProjectedSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [salariesRestricted, setSalariesRestricted] = useState(false);

  const { organisationId, currentOrganisation } = useAuth();
  const { currentProject } = useProject();
  const projectId = currentProject?.id;

  useEffect(() => {
    if (organisationId && projectId) {
      loadData();
    }
  }, [organisationId, projectId]);

  const calculateProjectedData = (
    data: ProjectSummaryData,
    taxYear: string,
    taxYearStartMonth: number
  ): ProjectedSummaryData => {
    // Parse tax year (e.g., "2024-25" -> start year 2024, start month from org)
    const [startYear] = taxYear.split('-');
    const { startDate, endDate } = calcProjectStartAndEndDates(
      startYear,
      taxYearStartMonth.toString()
    );

    const now = new Date();
    const isCurrentYear = now >= startDate && now <= endDate;

    if (!isCurrentYear) {
      // Not current year, return data as-is with no projection
      return {
        ...data,
        projectedTotalExpenses: data.totalExpenses,
        projectedTotalWages: data.totalWages,
        projectedTotalSuper: data.totalSuper,
        projectedTotalAssets: data.totalAssets,
        projectedTotalEquipment: data.totalEquipment,
        projectedGrandTotal: data.grandTotal,
        projectedTotalTaxOffset: data.totalTaxOffset,
        projectedActivities: data.activities,
        daysElapsed: 0,
        totalDays: 0,
        isCurrentYear: false,
      };
    }

    // Calculate days elapsed and total days
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysElapsed = Math.floor((now.getTime() - startDate.getTime()) / msPerDay);
    const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay);

    // Avoid division by zero
    if (daysElapsed === 0) {
      return {
        ...data,
        projectedTotalExpenses: data.totalExpenses,
        projectedTotalWages: data.totalWages,
        projectedTotalSuper: data.totalSuper,
        projectedTotalAssets: data.totalAssets,
        projectedTotalEquipment: data.totalEquipment,
        projectedGrandTotal: data.grandTotal,
        projectedTotalTaxOffset: data.totalTaxOffset,
        projectedActivities: data.activities,
        daysElapsed,
        totalDays,
        isCurrentYear: true,
      };
    }

    // Calculate velocity (spending per day) and project to year end
    const projectionMultiplier = totalDays / daysElapsed;

    const projectedActivities = data.activities.map((activity) => ({
      ...activity,
      expenses: activity.expenses * projectionMultiplier,
      wages: activity.wages * projectionMultiplier,
      super: activity.super * projectionMultiplier,
      assets: activity.assets * projectionMultiplier,
      equipment: activity.equipment * projectionMultiplier,
      total: activity.total * projectionMultiplier,
    }));

    const projectedTotalExpenses = data.totalExpenses * projectionMultiplier;
    const projectedTotalWages = data.totalWages * projectionMultiplier;
    const projectedTotalSuper = data.totalSuper * projectionMultiplier;
    const projectedTotalAssets = data.totalAssets * projectionMultiplier;
    const projectedTotalEquipment = data.totalEquipment * projectionMultiplier;
    const projectedGrandTotal = data.grandTotal * projectionMultiplier;
    const projectedTotalTaxOffset = projectedGrandTotal * (currentProject?.rebatePercentage || 0);

    return {
      ...data,
      projectedTotalExpenses,
      projectedTotalWages,
      projectedTotalSuper,
      projectedTotalAssets,
      projectedTotalEquipment,
      projectedGrandTotal,
      projectedTotalTaxOffset,
      projectedActivities,
      daysElapsed,
      totalDays,
      isCurrentYear: true,
    };
  };

  const loadData = async () => {
    try {
      if (!currentProject || !currentOrganisation) {
        return;
      }
      setLoading(true);
      setError(null);

      // Fetch activities first
      const activitiesData = await fetchProjectActivities(organisationId!, projectId!);
      setActivities(activitiesData.activities);

      // Fetch summary data for each category
      const [expensesResponse, salariesResponse, assetsResponse] = await Promise.all([
        // Backend applies org.allowedAccountTypes (default: [EXPENSE])
        apiClient.get(
          `/organisations/${organisationId}/projects/${projectId}/accounts?allocatedOnly=true`
        ),
        apiClient
          .get(`/organisations/${organisationId}/projects/${projectId}/salaries/employees`)
          .catch(() => null),
        apiClient.get(
          `/organisations/${organisationId}/projects/${projectId}/assets?allocatedOnly=true`
        ),
      ]);

      if (!expensesResponse.ok || !assetsResponse.ok) {
        throw new Error('Failed to fetch summary data');
      }

      const expensesData = await expensesResponse.json();
      const assetsData = await assetsResponse.json();

      // Salaries endpoint may return 403 if user doesn't have permission
      let salariesData = { success: false, employees: [] };
      if (salariesResponse?.ok) {
        salariesData = await salariesResponse.json();
        setSalariesRestricted(false);
      } else {
        setSalariesRestricted(true);
      }

      // Process the data to create activity summaries
      const activityMap = new Map<string, ActivitySummaryData>();

      // Initialize activities
      activitiesData.activities.forEach((activity) => {
        activityMap.set(activity.id, {
          activityId: activity.id,
          expenses: 0,
          wages: 0,
          super: 0,
          assets: 0,
          equipment: 0,
          total: 0,
        });
      });

      // Process expenses
      if (expensesData.success && expensesData.accounts) {
        expensesData.accounts.forEach((account: any) => {
          if (account.allocations) {
            account.allocations.forEach((allocation: any) => {
              const activity = activityMap.get(allocation.activityId);
              if (activity) {
                activity.expenses += allocation.amount || 0;
              }
            });
          }
        });
      }

      // Process wages and super
      if (salariesData.success && salariesData.employees) {
        salariesData.employees.forEach((employee: any) => {
          if (employee.allocations) {
            employee.allocations.forEach((allocation: any) => {
              const activity = activityMap.get(allocation.activityId);
              if (activity) {
                activity.wages += allocation.wages;
                activity.super += allocation.super;
              }
            });
          }
        });
      }

      // Process assets
      if (assetsData.success && assetsData.assets) {
        assetsData.assets.forEach((asset: any) => {
          if (asset.allocations && asset.projectAllocation > 0) {
            asset.allocations.forEach((allocation: any) => {
              const activity = activityMap.get(allocation.activityId);
              if (activity) {
                const depreciationAmount = asset.depreciationAmount || 0;
                const projectDepreciation = depreciationAmount * (asset.projectAllocation / 100);
                const activityDepreciation = projectDepreciation * (allocation.allocation / 100);
                activity.assets += activityDepreciation;
              }
            });
          }
        });
      }

      // Calculate totals for each activity
      const activities = Array.from(activityMap.values()).map((activity) => ({
        ...activity,
        total:
          activity.expenses +
          activity.wages +
          activity.super +
          activity.assets +
          activity.equipment,
      }));

      // Calculate project totals
      const baseSummary: ProjectSummaryData = {
        totalExpenses: activities.reduce((sum, a) => sum + a.expenses, 0),
        totalWages: activities.reduce((sum, a) => sum + a.wages, 0),
        totalSuper: activities.reduce((sum, a) => sum + a.super, 0),
        totalAssets: activities.reduce((sum, a) => sum + a.assets, 0),
        totalEquipment: activities.reduce((sum, a) => sum + a.equipment, 0),
        grandTotal: activities.reduce((sum, a) => sum + a.total, 0),
        totalTaxOffset: 0,
        activities,
      };
      baseSummary.totalTaxOffset = baseSummary.grandTotal * (currentProject.rebatePercentage || 0);

      // Calculate projected data
      const projectedData = calculateProjectedData(
        baseSummary,
        currentProject.taxYear,
        currentOrganisation.taxYearStartMonth || 7
      );

      setSummaryData(projectedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load summary data');
      console.error('Error loading summary data:', err);
    } finally {
      setLoading(false);
    }
  };

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);

  const getActivitySummary = (activityId: string): ActivitySummaryData | undefined => {
    return summaryData?.activities.find((a) => a.activityId === activityId);
  };

  const getProjectedActivitySummary = (activityId: string): ActivitySummaryData | undefined => {
    return summaryData?.projectedActivities.find((a) => a.activityId === activityId);
  };

  if (!currentProject) {
    return (
      <ContentWrapper>
        <EmptyStateContent
          icon={<AssessmentOutlined />}
          title="No project selected"
          message="Please select a project to view the summary."
        />
      </ContentWrapper>
    );
  }

  if (!organisationId || !projectId) {
    return (
      <ContentWrapper>
        <EmptyStateContent
          icon={<AssessmentOutlined />}
          title="No project selected"
          message="Please select a project to view the summary."
        />
      </ContentWrapper>
    );
  }

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="Project Report">
          <LoadingComponent context="Loading project summary..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  if (error) {
    return (
      <ContentWrapper>
        <PaperSection title="Project Report">
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<AssessmentOutlined />}
              title="Error loading summary"
              message={error}
              showBorder={false}
            />
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Project Report - ${currentProject?.name}`}
        action={
          <>
            <HelpComponent title="Project Cost Summary">
              <Typography variant="body2" paragraph>
                This summary shows the total allocated costs across all research activities for your
                R&D project.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Expenses:</strong> Transaction costs allocated to activities
                <br />
                <strong>Wages:</strong> Employee salary costs allocated to activities
                <br />
                <strong>Super:</strong> Superannuation costs allocated to activities
                <br />
                <strong>Assets:</strong> Asset depreciation allocated to activities
                <br />
                <strong>Equipment:</strong> Balance sheet equipment costs allocated to activities
              </Typography>
              {summaryData?.isCurrentYear && (
                <Typography variant="body2" paragraph>
                  <strong>Projected Totals:</strong> Based on current spending velocity (
                  {summaryData.daysElapsed} of {summaryData.totalDays} days elapsed)
                </Typography>
              )}
            </HelpComponent>
          </>
        }
      >
        {activities.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No research activities"
              message="Create research activities first to view project summary."
              button={
                <Button
                  component={RouterLink}
                  to={`/app/activities/${projectId}`}
                  variant="contained"
                  size="small"
                >
                  Create Your First Activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : !summaryData ? (
          <EmptyStateContent
            icon={<AssessmentOutlined />}
            title="No data available"
            message="No cost allocations found for this project."
          />
        ) : (
          <>
            {/* Summary Cards */}
            <Card sx={{ mb: 3, textAlign: 'center', p: 4, backgroundColor: 'white' }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ flexWrap: 'wrap', gap: 0, justifyContent: 'space-between' }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.50',
                    borderRadius: 2,
                    minWidth: 100,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" color="primary.main" fontWeight={700}>
                    {formatCurrency(summaryData.grandTotal)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Project Cost
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'primary.50',
                    borderRadius: 2,
                    minWidth: 100,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" color="success.main" fontWeight={700}>
                    {formatCurrency(summaryData.totalTaxOffset)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total R&D Tax Offset
                  </Typography>
                </Box>
                {summaryData.isCurrentYear && (
                  <>
                    <Box
                      sx={{
                        bgcolor: 'info.50',
                        borderRadius: 2,
                        minWidth: 100,
                        textAlign: 'center',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <TrendingUpOutlined sx={{ fontSize: 20, color: 'info.main' }} />
                        <Typography variant="h6" color="info.main" fontWeight={600}>
                          {formatCurrency(summaryData.projectedGrandTotal)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Projected Total
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: 'success.50',
                        borderRadius: 2,
                        minWidth: 100,
                        textAlign: 'center',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <TrendingUpOutlined sx={{ fontSize: 20, color: 'success.main' }} />
                        <Typography variant="h6" color="success.main" fontWeight={600}>
                          {formatCurrency(summaryData.projectedTotalTaxOffset)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Projected Rebate
                      </Typography>
                    </Box>
                  </>
                )}
                {!summaryData.isCurrentYear && (
                  <>
                    <Box
                      sx={{
                        bgcolor: 'success.50',
                        borderRadius: 2,
                        minWidth: 80,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h6" color="primary.main" fontWeight={600}>
                        {
                          sortedActivities.filter((a) => a.type === ResearchActivityType.CORE)
                            .length
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Core Activities
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: 'info.50',
                        borderRadius: 2,
                        minWidth: 80,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h6" color="primary.light" fontWeight={600}>
                        {
                          sortedActivities.filter((a) => a.type === ResearchActivityType.SUPPORTING)
                            .length
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supporting Activities
                      </Typography>
                    </Box>
                  </>
                )}
                <WorkbookDownloadButton variant="contained" size="small" fullWidth={false} />
              </Stack>
            </Card>

            {/* Summary Table */}
            <TableContainer
              component={Box}
              sx={{
                mx: -6,
                px: 6,
                width: 'calc(100% + 96px)',
                overflowX: 'scroll',
                '&::-webkit-scrollbar': {
                  WebkitAppearance: 'none',
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 0, 0, .3)',
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    '& .MuiTableCell-head': {
                      zIndex: (theme) => theme.zIndex.appBar + 1,
                      backgroundColor: 'background.paper',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      borderTop: '0px none',
                      fontWeight: 600,
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ width: 200, minWidth: 200 }}>Cost Category</TableCell>
                    {sortedActivities.map((activity) => (
                      <TableCell
                        key={activity.id}
                        sx={{ width: 120, minWidth: 120, p: 1, verticalAlign: 'top' }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                            size="small"
                            color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            align="center"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {activity.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{ width: 140, minWidth: 140, textAlign: 'right', bgcolor: 'primary.50' }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Expenses Row */}
                  <TableRow>
                    <TableCell>
                      <Button
                        component={RouterLink}
                        to={`/app/reports/${projectId}`}
                        variant="text"
                        size="small"
                        endIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Transaction Expenses
                      </Button>
                    </TableCell>
                    {sortedActivities.map((activity) => {
                      const summary = getActivitySummary(activity.id);
                      return (
                        <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">
                            {summary ? formatCurrency(summary.expenses) : '-'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(summaryData.totalExpenses)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Projected Expenses Row */}
                  {summaryData.isCurrentYear && (
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          opacity: 0.6,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          Projected Expenses
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const projected = getProjectedActivitySummary(activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                              {projected ? formatCurrency(projected.expenses) : '-'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {formatCurrency(summaryData.projectedTotalExpenses)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Wages Row */}
                  <TableRow>
                    <TableCell>
                      <Button
                        component={RouterLink}
                        to={`/app/salaries/${projectId}`}
                        variant="text"
                        size="small"
                        endIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Employee Salaries
                      </Button>
                    </TableCell>

                    {sortedActivities.map((activity) => {
                      const summary = getActivitySummary(activity.id);
                      return (
                        <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                          {salariesRestricted ? (
                            <Typography variant="caption" color="text.disabled" fontStyle="italic">
                              Insufficient permissions
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {summary ? formatCurrency(summary.wages) : '-'}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                      {salariesRestricted ? (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                          Insufficient permissions
                        </Typography>
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(summaryData.totalWages)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Projected Wages Row */}
                  {summaryData.isCurrentYear && !salariesRestricted && (
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          opacity: 0.6,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          Projected Salaries
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const projected = getProjectedActivitySummary(activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                              {projected ? formatCurrency(projected.wages) : '-'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {formatCurrency(summaryData.projectedTotalWages)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Super Row */}
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        Superannuation
                      </Typography>
                    </TableCell>
                    {sortedActivities.map((activity) => {
                      const summary = getActivitySummary(activity.id);
                      return (
                        <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                          {salariesRestricted ? (
                            <Typography variant="caption" color="text.disabled" fontStyle="italic">
                              -
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {summary ? formatCurrency(summary.super) : '-'}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                      {salariesRestricted ? (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                          -
                        </Typography>
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(summaryData.totalSuper)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Projected Super Row */}
                  {summaryData.isCurrentYear && !salariesRestricted && (
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          opacity: 0.6,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          Projected Super
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const projected = getProjectedActivitySummary(activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                              {projected ? formatCurrency(projected.super) : '-'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {formatCurrency(summaryData.projectedTotalSuper)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Assets Row */}
                  <TableRow>
                    <TableCell>
                      <Button
                        component={RouterLink}
                        to={`/app/depreciation/${projectId}`}
                        variant="text"
                        size="small"
                        endIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Asset Depreciation
                      </Button>
                    </TableCell>

                    {sortedActivities.map((activity) => {
                      const summary = getActivitySummary(activity.id);
                      return (
                        <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">
                            {summary ? formatCurrency(summary.assets) : '-'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(summaryData.totalAssets)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Projected Assets Row */}
                  {summaryData.isCurrentYear && (
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          opacity: 0.6,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          Projected Depreciation
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const projected = getProjectedActivitySummary(activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                              {projected ? formatCurrency(projected.assets) : '-'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {formatCurrency(summaryData.projectedTotalAssets)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Equipment Row (Placeholder) */}
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="text.disabled">
                        Equipment
                      </Typography>
                    </TableCell>
                    {sortedActivities.map((activity) => (
                      <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.disabled">
                          -
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.25' }}>
                      <Typography variant="body2" fontWeight={600} color="text.disabled">
                        -
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Totals Row */}
                  <TableRow
                    sx={{
                      bgcolor: 'grey.50',
                      '& .MuiTableCell-root': {
                        borderTop: '2px solid',
                        borderColor: 'divider',
                        fontWeight: 600,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        Total
                      </Typography>
                    </TableCell>
                    {sortedActivities.map((activity) => {
                      const summary = getActivitySummary(activity.id);
                      return (
                        <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {summary ? formatCurrency(summary.total) : '-'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ textAlign: 'right', bgcolor: 'primary.100' }}>
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        {formatCurrency(summaryData.grandTotal)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Projected Totals Row */}
                  {summaryData.isCurrentYear && (
                    <TableRow
                      sx={{
                        bgcolor: 'info.50',
                        '& .MuiTableCell-root': {
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          opacity: 0.7,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          Projected Total
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const projected = getProjectedActivitySummary(activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" fontWeight={600} color="info.main">
                              {projected ? formatCurrency(projected.total) : '-'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ textAlign: 'right', bgcolor: 'info.100' }}>
                        <Typography variant="body2" fontWeight={700} color="info.main">
                          {formatCurrency(summaryData.projectedGrandTotal)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={sortedActivities.length + 1}
                      sx={{ borderBottom: '0px', textAlign: 'right' }}
                    >
                      <Typography variant="body2">R&D Percentage</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="right"
                        justifyContent="right"
                      >
                        <Typography variant="body2">
                          {(currentProject.rebatePercentage || 0) * 100}%
                        </Typography>
                        <Tooltip title="Edit project R&D percentage" arrow>
                          <IconButton
                            component={RouterLink}
                            to={`/app/projects/${currentProject.id}`}
                            size="small"
                            sx={{ p: 0.25 }}
                          >
                            <EditOutlined sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={sortedActivities.length + 1}
                      sx={{ borderBottom: '0px', textAlign: 'right' }}
                    >
                      <Typography variant="body1" fontWeight={700} color="primary.main">
                        Total R&D Tax Offset
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" fontWeight={700} color="primary.main">
                        {formatCurrency(summaryData.totalTaxOffset)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {summaryData.isCurrentYear && (
                    <TableRow>
                      <TableCell
                        colSpan={sortedActivities.length + 1}
                        sx={{ borderBottom: '0px', textAlign: 'right' }}
                      >
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Projected R&D Tax Offset
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={600} color="info.main">
                          {formatCurrency(summaryData.projectedTotalTaxOffset)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableFooter>
              </Table>
            </TableContainer>

            {/* Footer Summary */}
            <Box
              sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                Summary of all allocated costs across {sortedActivities.length} research activities
                {summaryData.isCurrentYear && (
                  <>
                    {' '}
                    • Projections based on {summaryData.daysElapsed} of {summaryData.totalDays} days
                    elapsed
                  </>
                )}
              </Typography>
            </Box>
          </>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Summary;
